import React, { useState, useEffect } from 'react';
import { Plus, Trash2, Loader2, ImageOff } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import ImageUpload from './ImageUpload';
import { getGallery, createGalleryItem, deleteGalleryItem } from '../lib/api';
import { toast } from 'sonner';

const GalleryManager = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ image_url: '', title: '', category: 'exterior' });

  const load = async () => {
    setLoading(true);
    try {
      setItems(await getGallery());
    } catch (e) {
      toast.error('Error al cargar la galería');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const handleAdd = async (e) => {
    e.preventDefault();
    if (!form.image_url) {
      toast.error('Debes agregar una imagen');
      return;
    }
    setSaving(true);
    try {
      await createGalleryItem({ ...form, order: items.length });
      toast.success('Imagen agregada');
      setForm({ image_url: '', title: '', category: 'exterior' });
      setShowForm(false);
      load();
    } catch (e) {
      toast.error('Error al agregar imagen');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('¿Eliminar esta imagen?')) return;
    try {
      await deleteGalleryItem(id);
      toast.success('Imagen eliminada');
      setItems(items.filter((i) => i.id !== id));
    } catch (e) {
      toast.error('Error al eliminar');
    }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-2xl font-bold text-white">Galería</h2>
          <p className="text-white/60 text-sm">{items.length} imágenes publicadas</p>
        </div>
        <Button
          onClick={() => setShowForm(!showForm)}
          data-testid="add-gallery-button"
          className="bg-[#60A5FA] hover:bg-[#3B82F6] text-white rounded-xl"
        >
          <Plus className="w-4 h-4 mr-2" />
          Agregar imagen
        </Button>
      </div>

      {showForm && (
        <form onSubmit={handleAdd} className="bg-[#0F1D33]/60 border border-white/10 rounded-2xl p-6 mb-6 space-y-4">
          <ImageUpload
            value={form.image_url}
            onChange={(url) => setForm({ ...form, image_url: url })}
            label="Imagen de la galería"
            testId="gallery-image"
          />
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-white/70 text-sm mb-2 block">Título (opcional)</label>
              <Input
                value={form.title}
                onChange={(e) => setForm({ ...form, title: e.target.value })}
                placeholder="Ej: BMW Serie 3"
                data-testid="gallery-title"
                className="bg-[#0A1628]/60 border-white/20 text-white rounded-xl"
              />
            </div>
            <div>
              <label className="text-white/70 text-sm mb-2 block">Categoría</label>
              <select
                value={form.category}
                onChange={(e) => setForm({ ...form, category: e.target.value })}
                data-testid="gallery-category"
                className="w-full h-10 bg-[#0A1628]/60 border border-white/20 text-white rounded-xl px-3"
              >
                <option value="exterior">Exterior</option>
                <option value="interior">Interior</option>
                <option value="detalles">Detalles</option>
                <option value="general">General</option>
              </select>
            </div>
          </div>
          <div className="flex gap-3">
            <Button type="submit" disabled={saving} data-testid="save-gallery-button" className="bg-[#60A5FA] hover:bg-[#3B82F6] text-white rounded-xl">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Guardar'}
            </Button>
            <Button type="button" variant="outline" onClick={() => setShowForm(false)} className="border-white/20 text-white rounded-xl">
              Cancelar
            </Button>
          </div>
        </form>
      )}

      {loading ? (
        <div className="flex justify-center py-20">
          <Loader2 className="w-8 h-8 text-[#60A5FA] animate-spin" />
        </div>
      ) : items.length === 0 ? (
        <div className="text-center py-20 text-white/40">
          <ImageOff className="w-12 h-12 mx-auto mb-4" />
          <p>No hay imágenes todavía. Agrega la primera.</p>
        </div>
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
          {items.map((item) => (
            <div key={item.id} className="group relative rounded-xl overflow-hidden border border-white/10 aspect-square">
              <img src={item.image_url} alt={item.title} className="w-full h-full object-cover" />
              <div className="absolute inset-0 bg-gradient-to-t from-black/80 to-transparent opacity-0 group-hover:opacity-100 transition-opacity flex items-end p-3">
                <div className="w-full">
                  <p className="text-white text-sm font-medium truncate">{item.title || 'Sin título'}</p>
                  <p className="text-[#60A5FA] text-xs capitalize">{item.category}</p>
                </div>
              </div>
              <button
                onClick={() => handleDelete(item.id)}
                data-testid={`delete-gallery-${item.id}`}
                className="absolute top-2 right-2 w-8 h-8 bg-red-500/90 rounded-lg flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity hover:bg-red-600"
              >
                <Trash2 className="w-4 h-4 text-white" />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default GalleryManager;
